import { NavItem, NavListItemWithBlockDescription, Select, SelectProps } from '@billy/design-system'
import omit from 'lodash/omit'
import { ReactElement, useCallback, useMemo } from 'react'
import { useQuery } from 'react-query'

import { fetchAccount } from '@modules-deprecated/app/accounts/query-api'

import { QueryKeys } from '../../../../enums/queryKeys'
import { useSelectableProductAccounts } from '../../hooks/useSelectableProductAccounts'
import { SelectableProductAccount } from '../../query-api'
import { ProductAccountValue } from './types/productAccountValue'
import { getDisplayTitle } from './utils/getDisplayTitle'
import { getItems } from './utils/getItems'
import { getSelectableAccountsWithLegacy } from './utils/getSelectableAccountsWithLegacy'

export interface ProductAccountSelectProps
  extends Omit<SelectProps<ProductAccountValue>, 'items' | 'dropdownItemRender' | 'dropdownFetching' | 'onSelect'> {
  onSelect?: (id?: string, value?: SelectableProductAccount) => void
}

/**
 * Accounting account selector for products (STR-111). Unlike the generic `AccountSelect`, this is
 * backed by the platform's curated `GET /v4/products/selectable-accounts` endpoint (French PCG
 * revenue classes 70/75/76 only) instead of the full Billy chart of accounts.
 *
 * Since the curated list may not contain an already-selected legacy account (e.g. a product saved
 * before this restriction existed, or on a non-restricted account class), the currently selected
 * account is always kept visible/selectable even when it falls outside the curated list - mirroring
 * the "never drop the current selection" behavior of `AccountSelect`'s `filterAccounts`.
 */
export const ProductAccountSelect = ({
  onSelect,
  selectedId,
  ...selectProps
}: ProductAccountSelectProps): ReactElement => {
  const { accounts, isLoading } = useSelectableProductAccounts()

  const isLegacyAccountMissing = !!selectedId && !isLoading && !accounts.some((account) => account.id === selectedId)

  const { data: legacyAccount, isLoading: isLoadingLegacyAccount } = useQuery(
    [QueryKeys.Account, selectedId],
    () => fetchAccount(selectedId as string),
    { enabled: isLegacyAccountMissing },
  )

  const accountsWithLegacySelection = useMemo(
    () => getSelectableAccountsWithLegacy(accounts, legacyAccount?.account),
    [accounts, legacyAccount],
  )

  const items = useMemo(() => getItems(accountsWithLegacySelection), [accountsWithLegacySelection])

  const getDisplayValue = useCallback((item: NavItem<ProductAccountValue>) => getDisplayTitle(item.value), [])

  const handleSelect = useCallback(
    (id?: string, value?: ProductAccountValue) => {
      const valueFiltered = omit(value, 'displayData')
      onSelect?.(id, valueFiltered)
    },
    [onSelect],
  )

  return (
    <Select
      {...selectProps}
      dropdownFetching={isLoading || isLoadingLegacyAccount}
      dropdownItemRender={(props) => <NavListItemWithBlockDescription {...props} />}
      getDisplayValue={getDisplayValue}
      items={items}
      onSelect={handleSelect}
      selectedId={selectedId}
    />
  )
}
